import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * App-wide chat dock state: which view the floating panel is showing, for which
 * client, and whether it is open or expanded. Any component under the provider
 * can call `openChatFor(clientId)` to jump straight into a conversation.
 *
 * Mirrors `ClientProfileProvider` — context + `{children}` + a single overlay
 * mounted after them — so the two app-wide surfaces work the same way.
 *
 * Deliberately owns no room, message or roster data. The dock reads all of that
 * from `RoomsUnreadProvider` (`clientRoomIndex`, `unreadByRoom`) and
 * `roomMessagesCache`, both already populated app-wide, which is what makes
 * opening the dock instant rather than a fetch.
 */

/** Only UI preferences — never names or message content. See note below. */
const STORAGE_KEY = "flexboard.chatDock.v1";

/** @type {{isOpen: boolean, view: 'users'|'select'|'chat'|'notes', clientId: string|null, expanded: boolean}} */
const DEFAULT_STATE = {
  isOpen: false,
  view: "users",
  clientId: null,
  expanded: false,
};

const VIEWS = new Set(["users", "select", "chat", "notes"]);

/**
 * Views that address ONE client and cannot render without a `clientId`.
 *
 * `select` is deliberately not among them: it is a mode over the whole roster,
 * so `loadState` must not treat a persisted `select` as a dead view. It is
 * still discarded on reload — see the note there — but for a different reason.
 */
const CLIENT_VIEWS = new Set(["chat", "notes"]);

/**
 * Restore the persisted dock state, discarding anything corrupt or impossible
 * (e.g. a chat view with no client, which would render a dead panel).
 * @returns {typeof DEFAULT_STATE}
 */
function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    const clientId = parsed?.clientId == null ? null : String(parsed.clientId);
    const stored = VIEWS.has(parsed?.view) ? parsed.view : "users";
    // A client-scoped view without a client can't render — fall back to the
    // list. `select` is dropped too, for a different reason: the selection
    // itself lives in component state and is gone by now, so restoring the mode
    // would show an empty selection bar over a roster the coach didn't ask to
    // be in. Resuming a half-built recipient list across a reload is also not
    // something to do quietly when the next press sends to all of them.
    const view = CLIENT_VIEWS.has(stored) && !clientId ? "users" : stored;
    return {
      isOpen: Boolean(parsed?.isOpen),
      view: view === "select" ? "users" : view,
      clientId,
      expanded: Boolean(parsed?.expanded),
    };
  } catch {
    return DEFAULT_STATE;
  }
}

const ChatDockContext = createContext({
  ...DEFAULT_STATE,
  openDock: () => {},
  openChatFor: () => {},
  setView: () => {},
  back: () => {},
  toggleExpanded: () => {},
  close: () => {},
});

export const useChatDock = () => useContext(ChatDockContext);

export function ChatDockProvider({ children }) {
  const [state, setState] = useState(loadState);

  // Persisted so the dock survives a reload and route changes. Only the four
  // UI flags and an opaque client id go to disk: `utils/listCache/caches.js`
  // marks the user, profile and notes caches memory-only with an explicit PII
  // rationale, and the roster this dock renders is exactly that data. Names and
  // message previews stay in memory.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Persistence is a nicety; a full or blocked store must not break the dock.
    }
  }, [state]);

  const openDock = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  /**
   * Open the dock straight onto a client's conversation.
   * @param {string} clientId - FlexBoard user id.
   */
  const openChatFor = useCallback((clientId) => {
    if (clientId == null) return;
    setState((prev) => ({
      ...prev,
      isOpen: true,
      view: "chat",
      clientId: String(clientId),
    }));
  }, []);

  /** @param {'users'|'select'|'chat'|'notes'} view */
  const setView = useCallback((view) => {
    setState((prev) => (VIEWS.has(view) ? { ...prev, view } : prev));
  }, []);

  /** Chat/Notes/Select -> the roster. Clears the client so the list is the root. */
  const back = useCallback(() => {
    setState((prev) => ({ ...prev, view: "users", clientId: null }));
  }, []);

  const toggleExpanded = useCallback(() => {
    setState((prev) => ({ ...prev, expanded: !prev.expanded }));
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      openDock,
      openChatFor,
      setView,
      back,
      toggleExpanded,
      close,
    }),
    [state, openDock, openChatFor, setView, back, toggleExpanded, close]
  );

  return (
    <ChatDockContext.Provider value={value}>{children}</ChatDockContext.Provider>
  );
}

export default ChatDockProvider;
